<template lang="pug">
    .card
        .content

            i.right.floated.link.pencil.icon(
                v-if='isEditable'
                v-tooltip="'Adresse ändern'"
                v-on:click="showModal()"
            )

            .header Lieferadresse
            .meta(v-if='$auth.isZmuekish')
                router-link.ui.link(target='_blank' v-bind:to='routerLink') {{ addressModel.name }}

            .description
                .ui.list
                    .item(v-if="!isPrivate") {{ addressModel.firm }}
                    .item(v-if="!isPrivate") {{ addressModel.street }}
                    .item {{ addressModel.zip }} {{ addressModel.city }}
        .extra.content
            .ui.list
                .item
                    | #[strong Anzahl: ] {{ amount }}
                .item(v-if="$auth.isZmuekish")
                    | #[strong Ansprechperson: ] {{ contactPersonName }}
                .item
                    | #[strong Lieferbedingungen: ] {{ shippingAddressModel.deliveryConditions }}
                .item
                    | #[strong Avis: ] {{ advice }}

        modal(ref="modal" v-if="isEditable")
            span(slot="header") Adresse ändern

            div(slot="content").ui.form

                //- Adresse
                address-dropdown-field(
                    v-bind:address-models='addressModelOptions'
                    v-bind:company-model='companyModel'
                    v-bind:country-models='countryModels'
                    v-bind:force-shipping-address='true'
                    v-bind:person-models='personModels'
                    v-model="model.address"
                )

                //- Abweichende Ansprechperson
                person-dropdown-field(
                    label="Abweichende Ansprechperson"
                    v-bind:person-models='personModels'
                    v-bind:company-model='companyModel'
                    v-model='contactId'
                )

                //- Anzahl
                number-field(label="Anzahl" v-model.number="model.amount" v-bind:min='0')

                //- Lieferbedingungen
                textarea-field(label="Lieferbedingungen" v-model.trim="model.deliveryConditions")

            action-row(
                slot="footer"
                v-on:cancel="closeModal()"
                v-on:save="save()"
            )
                a.ui.red.labeled.icon.button(v-if="isDeletable" v-on:click='remove()')
                    i.trash.icon
                    | Entfernen


</template>

<script>
    import * as R from 'ramda'
    import store from '@/store'
    import * as AddressUtils from '@/utils/address'
    import { full_name } from '@/utils/person'
    import ActionRow from '@/components/form/ActionRow'
    import AddressDropdownField from '@/components/addresses/DropdownField'
    import Modal from '@/components/app/Modal'
    import NumberField from '@/components/form/NumberField.vue'
    import PersonDropdownField from '@/components/persons/DropdownField'
    import TextareaField from '@/components/form/TextareaField'

    export default {

        components: { ActionRow, AddressDropdownField, Modal, NumberField, PersonDropdownField, TextareaField },

        computed: {

            addressModel() {
                return R.find(R.propEq('id', this.shippingAddressModel.address), this.addressModels) || {}
            },

            addressModelOptions() {
                return R.compose(

                    // Namen von Standardlieferadresse formatieren
                    R.map(AddressUtils.format_name_by_default_shipping_address),

                    // Nach Standard-Lieferadresse sortieren
                    AddressUtils.sort_by_is_default_shipping_address

                )(this.addressModels)
            },

            advice() {
                return R.cond([
                    [R.equals('NO'), R.always('Nein')],
                    [R.equals('ONE_DAY'), R.always('1 Tag')],
                    [R.equals('TWO_DAYS'), R.always('2 Tage')],
                    [R.equals('THREE_DAYS'), R.always('3 Tage')],
                ])(this.addressModel.advice)
            },

            amount() {
                return R.compose(

                    // ggf. Default-Wert setzen
                    R.defaultTo('Komplett'),

                    // Anzahl auslesen
                    R.prop('amount')

                )(this.shippingAddressModel)
            },

            contactId: {
                get() { return R.defaultTo(0, this.model.contact) },
                set(value) { this.$set(this.model, 'contact', value) },
            },

            contactPersonName() {
                if (R.isNil(this.companyModel.id)) {
                    return null
                }

                // Name des Warenempfängers der Standard-Rechnungsadresse
                const defaultContactName = R.compose(
                    R.prop('contactName'),
                    store.getters['addresses/byId'],
                    R.prop('defaultBillingAddress')
                )(this.companyModel)

                // Abweichende Ansprechperson der Lieferadresse
                const overridePerson = R.compose(
                    personId => R.isNil(personId) ? null : this.personModels.find(personModel => personModel.id === personId),
                    R.prop('contact')
                )(this.shippingAddressModel)

                return (overridePerson ? full_name(overridePerson) : null) || defaultContactName
            },

            routerLink() {
                return {
                    name: 'addresses/update',
                    params: { id: this.addressModel.id }
                }
            },

        },

        created() {
            this.model = { ...this.shippingAddressModel }
        },

        data() {
            return { model: {} }
        },

        methods: {
            closeModal() {
                this.$refs.modal.close()
            },

            remove() {
                if (confirm(`Die Lieferadresse "${this.addressModel.name}" wirklich löschen?`)) {
                    store.dispatch('shippingAddresses/remove', { model: this.shippingAddressModel })
                        .then(() => this.$growl.success('Lieferadresse wurde gelöscht'))
                        .catch(() => this.$growl.error('Lieferadresse konnte nicht gelöscht werden'))
                }
            },

            save() {
                // Model klonen
                const model = { ...this.model }

                // Wenn Anzahl 0 ist, dann `null`en
                if (model.amount === 0) {
                    model.amount = null
                }

                store.dispatch('shippingAddresses/update', { model }).then(() => {
                    this.closeModal()
                    this.$growl.success('Lieferadresse aktualisiert')
                }).catch(() => {
                    this.$growl.error('Lieferadresse konnte nicht aktualisiert werden')
                })
            },

            showModal() {
                this.$refs.modal.show()
            }
        },

        props: {

            addressModels: {
                type: Array,
                required: true
            },

            companyModel: {
                type: Object,
                default: R.always({})
            },

            countryModels: {
                type: Array,
                required: false
            },

            isDeletable: {
                type: Boolean,
                default: true
            },

            isEditable: {
                type: Boolean,
                default: true,
            },

            isPrivate: {
                type: Boolean,
                default: false
            },

            personModels: {
                type: Array,
                required: true
            },

            shippingAddressModel: {
                type: Object,
                required: true
            },

        }

    }

</script>
